import { useState, useEffect, useCallback } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { getUser } from '@/lib/common';

export interface AllocationResource {
  id: number;
  resource_id: number | null;
  resource_name: string;
  resource_type: string;
  subscription_name: string;
  resource_group_name: string;
  cloud_account_name: string;
  cost: number;
  currency: string;
  owner_id: number | null;
  owner_name: string | null;
  system_id: number | null;
  system_name: string | null;
  cloud_account_id: number;
  cloud_account_owner_name: string | null;
}

export interface TopUnallocatedResource {
  id: number;
  name: string;
  type: string;
  cost: number;
  subscription: string;
  resourceGroup: string;
  cloudAccount: string;
  currency: string;
}

export interface UnallocatedByOwner {
  ownerName: string;
  cost: number;
  resourceCount: number;
  currency: string;
}

interface AllocationData {
  resources: AllocationResource[];
}

export const useAllocation = () => {
  const [allocationData, setAllocationData] = useState<AllocationData>({
    resources: [],
  });
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState<string | null>(null);

  const user = getUser();

  const refetchAllocationData = useCallback(async () => {
    if (!user?.id) return;

    setDataLoading(true);
    setDataError(null);

    try {
      const currency = user?.default_currency || 'USD';

      const allocationResponse = await fetchWrapper.get(
        urls.DevOps.getAttestationTable + `?currency=${currency}`,
      );

      const allResources = allocationResponse || [];

      setAllocationData({
        resources: allResources.sort((a: any, b: any) =>
          a.cost < b.cost ? 1 : -1,
        ),
      });
    } catch (err) {
      console.error('Error refetching allocation data:', err);
      setDataError('Failed to fetch allocation data');
    } finally {
      setDataLoading(false);
    }
  }, [user?.id, user?.default_currency]);

  useEffect(() => {
    refetchAllocationData();
  }, [user?.id, user?.default_currency, refetchAllocationData]);

  return {
    data: allocationData,
    loading: dataLoading,
    error: dataError,
    refetch: refetchAllocationData,
    user,
  };
};
